import React, { useState } from 'react';
import { Mail, MapPin, Phone, Clock, Send } from 'lucide-react';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    // You can add form submission logic here
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-primary-500 text-secondary-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-4 animate-fade-in">
              Contact Us
            </h1>
            <p
              className="text-lg md:text-2xl text-secondary-100 max-w-3xl mx-auto animate-slide-up"
              style={{ animationDelay: '0.2s' }}
            >
              Get in touch with our engineering experts
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8 animate-fade-in-left">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-primary-500 mb-6">
                Get In Touch
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-8">
                Ready to discuss your engineering needs? Our team of experts is
                here to help you find the perfect solution for your project.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div
                className="flex items-start space-x-4 animate-slide-up"
                style={{ animationDelay: '0.3s' }}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <Mail className="h-6 w-6 text-primary-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Email</h3>
                  <p className="text-gray-600">info@harmonyengg.com</p>
                </div>
              </div>

              <div
                className="flex items-start space-x-4 animate-slide-up"
                style={{ animationDelay: '0.4s' }}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <Phone className="h-6 w-6 text-primary-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Phone</h3>
                  <p className="text-gray-600">+968 9710 0143 / 9454 9531</p>
                  <p className="text-sm text-gray-500">
                    Call us for immediate assistance
                  </p>
                </div>
              </div>

              <div
                className="flex items-start space-x-4 animate-slide-up"
                style={{ animationDelay: '0.5s' }}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-primary-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Address</h3>
                  <p className="text-gray-600">
                    CR NO. 1601293
                    <br />
                    OUHI INDUSTRIAL AREA
                    <br />
                    SOHAR, SULTANATE OF OMAN
                  </p>
                </div>
              </div>

              <div
                className="flex items-start space-x-4 animate-slide-up"
                style={{ animationDelay: '0.6s' }}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-primary-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">
                    Business Hours
                  </h3>
                  <p className="text-gray-600">
                    Sunday - Thursday: 8:00 AM - 6:00 PM
                    <br />
                    Friday - Saturday: Closed
                  </p>
                </div>
              </div>
            </div>

            {/* Why Contact Us */}
            <div
              className="bg-primary-50 p-6 rounded-lg animate-slide-up"
              style={{ animationDelay: '0.7s' }}
            >
              <h3 className="text-xl font-bold text-primary-500 mb-4">
                Why Choose Us?
              </h3>
              <ul className="space-y-2 text-sm md:text-base text-gray-700">
                <li className="flex items-start space-x-2">
                  <span className="text-primary-500">•</span>
                  <span>Expert engineering consultation</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary-500">•</span>
                  <span>Custom solutions for your needs</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary-500">•</span>
                  <span>Competitive pricing</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary-500">•</span>
                  <span>Timely project delivery</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-8 rounded-lg shadow-lg animate-fade-in-right">
            <h2 className="text-2xl md:text-3xl font-bold text-primary-500 mb-6">
              Send Us a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="+968 XXXX XXXX"
                  />
                </div>
                <div>
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Your company name"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="service"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Service of Interest
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                >
                  <option value="">Select a service</option>
                  <option value="design-engineering">Design Engineering</option>
                  <option value="fabrication-erection">
                    Fabrication & Erection
                  </option>
                  <option value="machining">Machining</option>
                  <option value="plant-maintenance">Plant Maintenance</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-vertical"
                  placeholder="Tell us about your project requirements..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-primary-500 text-secondary-500 px-8 py-4 rounded-lg font-semibold hover:bg-primary-600 transition-colors duration-200 flex items-center justify-center space-x-2 cursor-pointer"
              >
                <Send className="h-5 w-5" />
                <span>Send Message</span>
              </button>
            </form>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 text-center">
                We typically respond within 24 hours. For urgent inquiries,
                please call us directly.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="text-center mb-12 animate-fade-in"
            style={{ animationDelay: '0.8s' }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-primary-500 mb-4">
              Visit Our Location
            </h2>
            <p className="text-base md:text-lg text-gray-700">
              Located in the heart of Sohar's industrial area
            </p>
          </div>

          {/* Google Map Embed */}
          <div
            className="rounded-lg shadow-lg overflow-hidden aspect-[16/9] animate-slide-up"
            style={{ animationDelay: '0.9s' }}
          >
            <iframe
              title="Sohar Industrial Area Location"
              src="https://www.google.com/maps/embed?pb=!1m13!1m8!1m3!1d22259.02147291144!2d56.6626091!3d24.3769214!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjTCsDIyJzM1LjciTiA1NsKwMzknNTQuMCJF!5e1!3m2!1sen!2sin!4v1762445188610!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-primary-500 text-secondary-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-2xl md:text-3xl font-bold mb-4 animate-fade-in"
            style={{ animationDelay: '1.0s' }}
          >
            Ready to Start Your Project?
          </h2>
          <p
            className="text-lg md:text-xl text-secondary-100 mb-8 max-w-2xl mx-auto animate-slide-up"
            style={{ animationDelay: '1.1s' }}
          >
            Let's discuss how we can help bring your engineering vision to life.
          </p>
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in"
            style={{ animationDelay: '1.2s' }}
          >
            <a
              href="mailto:info@harmonyengg.com"
              className="bg-secondary-500 text-primary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-100 transition-colors cursor-pointer"
            >
              Email Us Now
            </a>
            <a
              href="/services"
              className="border-2 border-secondary-500 text-secondary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-500 hover:text-primary-500 transition-colors cursor-pointer"
            >
              View Our Services
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
